import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import chestImage from '@/assets/images/heroes/lizcoin/chest.webp';
import securityImage from '@/assets/images/heroes/lizcoin/security.webp';
import safeImage from '@/assets/images/heroes/lizcoin/safe.webp';
import { cn } from '@/lib/utils';
import { isMobile } from 'react-device-detect';
import Stars from '@/components/stars';

interface BenefitType {
    image: string;
    imageClass: string;
    title: string;
    altText: string;
    description: string | JSX.Element[] | JSX.Element;
}

const benefits: BenefitType[] = [
    {
        image: chestImage,
        imageClass: "-mt-8",
        title: "Ownership",
        altText: "A treasure chest with an open lid full of shiny Lizcoins.",
        description: [
            <p key="rw_1"><b>Shape The Future:</b> Own your share of the Lizard Labs Gaming DAO, and use Lizcoin to make your opinion count.</p>,
            <p key="rw_2"><b>Revenue Sharing:</b> Active stakers receive a portion of the revenue generated from our products and services.</p>
        ]
    },
    {
        image: securityImage,
        imageClass: "-mt-8",
        altText: "A strong-looking security guard Lizard guarding access to a velvet rope entrance.",
        title: "Access",
        description: [
            <p key="ea_1"><b>Wagering and Gaming:</b> Unlock wagering in Lizard League and access exclusive gaming experiences.</p>,
            <p key="ea_2"><b>Exclusive Value:</b> Can only be purchased with Lizcoin, including tournament entries, special events, and in-game assets.</p>,
        ]
    },
    {
        image: safeImage,
        imageClass: "-mb-8",
        altText: "A sneaky lizard using a listening device to help hack a locked safe.",
        title: "Rewards",
        description: [
            <p key="pp_1"><b>Exclusive Opportunities:</b> Play-4-Airdrop to receive partner tokens as rewards.</p>,
            <p key="pp_2"><b>Staking Loyalty Rewards:</b> Earn rewards by staking your Lizcoin.</p>
        ]
    }
]

const BenefitCard = ({ benefit }: { benefit: BenefitType }) => {
    return isMobile ? (
        <div className='relative flex flex-col w-full items-center min-h-[300px] rounded-[20px]'>
            <div className='absolute top-40 left-0 right-0 bottom-0 bg-benefit-lizcoin-vertical opacity-80 rounded-[20px]' />
            <Stars className='absolute opacity-5 min-w-full h-dvh fill-yellow' />
            <motion.img src={benefit.image} className='relative w-[60%] max-h-[300px] mx-auto' alt={benefit.altText} />
            <div className='flex flex-col items-start text-left gap-y-6 p-6'>
                <h3 className='font-nekstbold text-[24px] lg:!text-[36px] !leading-7 lg:!leading-10 text-yellow'>{benefit.title}</h3>
                <div className='flex flex-col text-white gap-y-5 !font-figtree text-base'>
                    {benefit.description}
                </div>
            </div>
        </div>
    ) : (
        <div className='relative flex w-full items-center min-h-[300px] rounded-[20px]'>
            <div className='absolute top-0 left-32 right-0 bottom-0 bg-benefit-lizcoin opacity-80 rounded-[20px]' />
            <div className='absolute flex h-full items-center'>
                <motion.img alt={benefit.altText} src={benefit.image} className={cn('relative max-w-[300px] max-h-[300px]', benefit.imageClass ? benefit.imageClass : '')}/>
            </div>
            <div className='flex flex-col items-start text-left gap-y-6 pl-[calc(150px_+_14rem)] max-w-[90%] lg:max-w-[85%]'>
                <h3 className='font-nekstbold text-[24px] lg:!text-[36px] !leading-7 lg:!leading-10 text-yellow'>{benefit.title}</h3>
                <div className='flex flex-col text-white gap-y-5 !font-figtree text-base'>
                    {benefit.description}
                </div>
            </div>
        </div>
    )
}

const LizcoinBenefits = () => {

    const ref = useRef(null);
    const isInView = useInView(ref);

    const [hasAnimated, setHasAnimated] = useState(false);

    useEffect(() => {
        if (isInView) setHasAnimated(true);
    }, [isInView])

    return (
        <section 
            id="lizcoin-benefits"
            className="relative z-[1] md:z-0 flex flex-col justify-center items-start md:items-center md:justify-between md:flex-row gap-y-8 lg:py-20 px-6 w-full"
        >
            <div ref={ref} className='relative flex flex-col gap-y-8 md:gap-x-8 items-center justify-center w-full lg:py-20'>
                {benefits.map((bnft, x) => (
                    <motion.div 
                        key={`lizcoin_benefit_${x}`} 
                        className="flex flex-col text-center gap-y-6 rounded-[10px] overflow-hidden w-full max-w-6xl"
                        transition={{ duration: 1.5, bounceStiffness: 5, bounceDamping: 5, bounce: 0.4, type: 'spring', delay: x * 0.15 }}
                        animate={hasAnimated ? "show" : "hide"}
                        variants={{
                            show: {
                                opacity: 1,
                                y: 0
                            },
                            hide: {
                                opacity: 0,
                                y: -80
                            }
                        }}
                    >
                        <BenefitCard key={`lizcoint_benefit_card_${x}`} benefit={bnft} />
                    </motion.div>
                ))}
            </div>
        </section>
    );
};

export default LizcoinBenefits;